/**
 * Hook useCalendarUnifiedEventsInfinite
 *
 * Variante paginada (scroll infinito) de useCalendarUnifiedEvents.
 * Reusa a mesma RPC public.get_calendar_unified_events (v1.8.0+),
 * ativando o modo cursor via p_limit + (p_cursor_start_at, p_cursor_id).
 *
 * Usado APENAS pela visualização em Lista da página /tenant/calendar,
 * para paridade com a tela Agenda (useSchedulingListActivities).
 * O modo Calendário (Mês/Semana/Dia) continua usando o hook não paginado.
 *
 * RELEASE:
 *   2026-07-24 v1.0.0 - Versão inicial (espelha useSchedulingListActivities).
 */

import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type {
  CalendarEventSourceType,
  UnifiedCalendarEvent,
} from '@/hooks/useCalendarUnifiedEvents';

const logger = createLogger('useCalendarUnifiedEventsInfinite');

/** Tamanho de página do scroll infinito (paridade com useSchedulingListActivities). */
const PAGE_SIZE = 30;

/** Cursor composto (start_at, id) para paginação server-side estável. */
interface CursorParam {
  cursor_start_at: string;
  cursor_id: string;
}

interface UseInfiniteParams {
  /** Início da janela (ISO). */
  start: string;
  /** Fim da janela (ISO). */
  end: string;
  /** Calendários selecionados; null = todos visíveis. */
  calendarIds?: string[] | null;
  /** Filtro de fontes; null = todas. */
  sourceTypes?: CalendarEventSourceType[] | null;
  /** Toggle "Mostrar concluídos/cancelados" (default TRUE). */
  includeFinished?: boolean;
  /** Habilita/desabilita a query. */
  enabled?: boolean;
}

/**
 * useCalendarUnifiedEventsInfinite
 *
 * Retorna a mesma forma dos hooks de scroll infinito do projeto
 * (events, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage, refetch).
 */
export function useCalendarUnifiedEventsInfinite(params: UseInfiniteParams) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  const {
    start,
    end,
    calendarIds,
    sourceTypes,
    includeFinished = true,
    enabled = true,
  } = params;

  const infiniteQuery = useInfiniteQuery({
    queryKey: [
      'calendar-unified-events-infinite',
      tenantId,
      start,
      end,
      calendarIds ?? 'all',
      sourceTypes ?? 'all',
      includeFinished,
    ],
    enabled: !!tenantId && !!start && !!end && enabled,
    staleTime: 30_000,
    initialPageParam: null as CursorParam | null,
    queryFn: async ({ pageParam }) => {
      const cursor = pageParam as CursorParam | null;
      logger.debug('Buscando página de eventos unificados', { cursor });

      const { data, error } = await supabase.rpc('get_calendar_unified_events', {
        p_start: start,
        p_end: end,
        p_calendar_ids: calendarIds ?? null,
        p_source_types: sourceTypes ?? null,
        p_timezone: 'America/Sao_Paulo',
        p_tenant_hint: tenantId ?? null,
        p_include_finished: includeFinished,
        // v1.8.0 — cursor pagination opt-in
        p_limit: PAGE_SIZE,
        p_cursor_start_at: cursor?.cursor_start_at ?? null,
        p_cursor_id: cursor?.cursor_id ?? null,
      } as never);

      if (error) {
        logger.error('Erro ao buscar página de eventos unificados', error);
        throw error;
      }

      const items = (data ?? []) as unknown as UnifiedCalendarEvent[];
      return { items };
    },
    getNextPageParam: (lastPage) => {
      // Se veio menos que PAGE_SIZE, é a última página.
      if (!lastPage || lastPage.items.length < PAGE_SIZE) return undefined;
      const last = lastPage.items[lastPage.items.length - 1];
      if (!last) return undefined;
      return {
        cursor_start_at: last.start_at,
        cursor_id: last.id,
      } as CursorParam;
    },
  });

  // Flatten das páginas em um array único, mantendo a ordem retornada pela RPC.
  const events = useMemo(
    () => infiniteQuery.data?.pages.flatMap((p) => p.items) ?? [],
    [infiniteQuery.data?.pages],
  );

  return {
    /** Lista achatada de todos os eventos carregados até agora. */
    events,
    /** Carregamento inicial. */
    isLoading: infiniteQuery.isLoading,
    /** Ainda há mais páginas a carregar. */
    hasNextPage: infiniteQuery.hasNextPage,
    /** Dispara o carregamento da próxima página. */
    fetchNextPage: infiniteQuery.fetchNextPage,
    /** Indica que uma página adicional está sendo carregada. */
    isFetchingNextPage: infiniteQuery.isFetchingNextPage,
    /** Refetch completo. */
    refetch: infiniteQuery.refetch,
  };
}
